import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { ROLE_LABEL } from '../access/roles.ts';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { invite } from '../users/api.ts';
import { loadOrgMembers, removeViewer, type OrgMember } from './api.ts';
import { TableWrap } from '../components/TableWrap.tsx';

export interface ClientUsersScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly clientOrgId: string;
  readonly clientOrgName: string;
  readonly currentUserId: string;
  readonly redirectTo: string;
  /** Preview as client: show the list only, with no control that changes anything. */
  readonly readOnly?: boolean;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const who = (m: OrgMember): string => m.displayName || m.email || 'Unnamed user';

/**
 * Users & roles for a client admin (docs/specs/portal/client-area.md §8): the members of their organisation, invite a
 * viewer (invite-user), remove a viewer. Admins are managed by the survey company.
 */
export function ClientUsersScreen({
  sb,
  tenantId,
  clientOrgId,
  clientOrgName,
  currentUserId,
  redirectTo,
  readOnly = false,
}: ClientUsersScreenProps): ReactElement {
  const [members, setMembers] = useState<OrgMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [inviting, setInviting] = useState(false);
  const [confirm, setConfirm] = useState<OrgMember | null>(null);

  const reload = useCallback(() => {
    setError(null);
    loadOrgMembers(sb, clientOrgId)
      .then(setMembers)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, clientOrgId]);
  useEffect(reload, [reload]);

  return (
    <section className="page">
      <PageHeader
        title="Users & roles"
        subtitle={`People of ${clientOrgName} who can sign in to this portal.`}
      >
        {!readOnly && (
          <Button variant="primary" onClick={() => setInviting(true)} disabled={inviting}>
            Invite viewer
          </Button>
        )}
      </PageHeader>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {inviting && !readOnly && (
        <InviteViewer
          onCancel={() => setInviting(false)}
          onSubmit={async (email) => {
            const res = await invite(
              sb,
              {
                tenant_id: tenantId,
                email,
                kind: 'client',
                role: 'client_viewer',
                client_org_id: clientOrgId,
              },
              redirectTo,
            );
            setInviting(false);
            setNotice({
              ok: true,
              text:
                res.status === 'invited'
                  ? `Invitation sent to ${email}.`
                  : `${email} already had an account and can now see ${clientOrgName}'s projects.`,
            });
            reload();
          }}
        />
      )}
      {confirm && !readOnly && (
        <div className="panel confirm" role="dialog" aria-labelledby="viewer-confirm-title">
          <p id="viewer-confirm-title" className="body">
            Remove {who(confirm)} from {clientOrgName}? They lose access to this portal straight away.
          </p>
          <div className="row">
            <Button
              className="btn--danger"
              onClick={() => {
                const m = confirm;
                setConfirm(null);
                removeViewer(sb, clientOrgId, m.userId)
                  .then(() => setNotice({ ok: true, text: `${who(m)} removed.` }))
                  .catch((e: unknown) =>
                    setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) }),
                  )
                  .finally(reload);
              }}
            >
              Remove viewer
            </Button>
            <Button variant="quiet" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!members && !error && <Loading what="members" />}
      {members && (
        <TableWrap>
          <table className="table">
            <thead>
              <tr>
                <th className="label">Name</th>
                <th className="label">Email</th>
                <th className="label">Role</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.userId}>
                  <td>{m.displayName || '–'}</td>
                  <td className="data">{m.email ?? 'Email not visible'}</td>
                  <td>{ROLE_LABEL[m.role]}</td>
                  <td className="table__actions">
                    {!readOnly && m.role === 'client_viewer' && m.userId !== currentUserId && (
                      <Button variant="quiet" aria-label={`Remove ${who(m)}`} onClick={() => setConfirm(m)}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      <p className="caption muted">
        Viewers see the projects, progress and published reports of {clientOrgName}. To add or remove a client
        admin, ask your survey company.
      </p>
    </section>
  );
}

function InviteViewer({
  onSubmit,
  onCancel,
}: {
  readonly onSubmit: (email: string) => Promise<void>;
  readonly onCancel: () => void;
}): ReactElement {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!EMAIL.test(addr)) {
      setError('Enter a valid email address, for example jane@example.com.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit(addr);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel stack" onSubmit={(e) => void submit(e)} aria-label="Invite viewer" noValidate>
      <h2 className="heading">Invite viewer</h2>
      <label className="label muted" htmlFor="viewer-email">
        Email
      </label>
      <input
        id="viewer-email"
        className="input"
        type="email"
        value={email}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? 'viewer-email-error' : undefined}
        onChange={(e) => setEmail(e.target.value)}
      />
      {error && (
        <p id="viewer-email-error" className="field__error caption" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <Button type="submit" variant="primary" busy={busy}>
          {busy ? 'Sending invitation…' : 'Send invitation'}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
